import { Drawer, Form, Input, InputNumber, Select, Switch } from "antd";
import { AppButton } from "components/button/AppButton";
import { COUNTRIES, GENDERS } from "constants/general";
import { useGetSectors } from "features/jobs/hooks/useGetSectors";
import { useState } from "react";
import { useGlobalContext } from "stateManagers/GlobalContext";
import { IdentifierProps } from "types";
import { openNotification } from "utils/notification";

export const FilterModal = ({ open, handleClose }: IdentifierProps) => {
  const { data: sectorData, isLoading: loadingSector } = useGetSectors();
  const { filterTalent, setFilterTalent } = useGlobalContext();
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [form] = Form.useForm();

  const handleSubmit = (data: any) => {
    const hasAtLeastOneFilter = Object.values(data).some(
      (val) => val !== undefined && val !== "",
    );

    if (hasAtLeastOneFilter) {
      setFilterTalent({
        ...filterTalent,
        sector: data.sector,
        experience: data.yearsOfEx,
        gender: data.gender,
        country: data.location,
        skills: data.skills,
        jobTitle: data.title,
        visibility_score: data.visibilityScore,
        assessmentStatus: data.assessmentStatus,
        relocate: data.relocate ?? null,
      });
      handleClose();
    } else {
      openNotification({
        state: "error",
        title: "Error",
        description: "Please filter with at least 1 value",
        duration: 4.5,
      });
    }
  };

  const handleReset = () => {
    form.resetFields();
    handleClose();
    setFilterTalent({
      sector: null,
      experience: null,
      gender: null,
      location: null,
      skills: null,
      jobTitle: null,
      visibilityScore: null,
      assessmentStatus: null,
      relocate: null,
    });
  };

  const handleCountryChange = async (value: string | null) => {
    if (!value) {
      setSelectedCountry(null);

      return;
    }
    setSelectedCountry(value);
  };

  return (
    <Drawer open={open} onClose={() => handleClose()} title="Filter Talent">
      <Form layout="vertical" form={form} onFinish={handleSubmit}>
        <Form.Item name="sector" label="Sector">
          <Select
            loading={loadingSector}
            allowClear
            placeholder="Select"
            filterOption={(input, option) => {
              return (
                option?.label?.toLowerCase().includes(input.toLowerCase()) ||
                false
              );
            }}
            options={sectorData?.map((item) => ({
              label: item.name,
              value: item.id,
            }))}
          />
        </Form.Item>

        <Form.Item name="yearsOfEx" label="Years of Experience">
          <Input className="w-full sm:w-auto" />
        </Form.Item>

        <Form.Item name="location" label="Location (Country)">
          <Select
            allowClear
            placeholder="Select"
            showSearch
            options={COUNTRIES.map((country) => ({
              value: country.name,
              label: country.name,
            }))}
            onChange={handleCountryChange}
            onClear={() => {
              setSelectedCountry(null);
            }}
          />
        </Form.Item>

        <Form.Item name="title" label="Job Title Keywords">
          <Input placeholder="e.g. Frontend Developer, Product Manager..." />
        </Form.Item>

        <Form.Item
          name="relocate"
          label="Willing to Relocate"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <div className="flex items-center justify-end gap-4 mt-4">
          <AppButton
            type="button"
            label="Reset"
            variant="transparent"
            handleClick={handleReset}
          />
          <AppButton type="submit" label="Apply" />
        </div>
      </Form>
    </Drawer>
  );
};
